import Decimal from "break_infinity.js";
import { newGame } from "../provider/GameProvider";
import { Game, Producer, psStartMultiplier } from "../types/game";
import {
  calculateExpectedMoleculeValue,
  calculateNewProducerStrengthiningMultiplier,
  getParticleStartValue,
  getPsToMultiplyId,
  isMoleculeUnlocked,
} from "./calculator";

export default function executeProducerStrengthening(props: {
  setGame: React.Dispatch<React.SetStateAction<Game>>;
  game: Game;
  setProgressTimes: any[];
}) {
  const psUpgradeLevel = props.game.producerStrengthening.upgradeLevel;
  const toMultiplyId = getPsToMultiplyId(
    psUpgradeLevel,
    props.game.producers.length
  );
  const multiplier = props.game.producerStrengthening.multiplier;
  const particleStartValue = getParticleStartValue(props.game);
  const effectsAllProducers = isMoleculeUnlocked("Ammonia", props.game);

  props.setGame((prev: Game) => ({
    ...prev,
    producers: prev.producers.map((producer: Producer, index: number) => {
      let newMultiplier = producer.multiplier;
      if (toMultiplyId && multiplier) {
        if (index === toMultiplyId || effectsAllProducers) {
          newMultiplier = producer.multiplier.times(multiplier);
        }
      }
      return {
        ...producer,
        value: index === 0 ? new Decimal(particleStartValue) : new Decimal(0),
        upgradeLevel: 0,
        produceTimeProgress: 0,
        multiplier: newMultiplier,
        unlocked: isMoleculeUnlocked("Nitrogen", prev)
          ? true
          : index <= psUpgradeLevel + 5
          ? true
          : producer.unlocked,
      };
    }),
    producerStrengthening: {
      ...prev.producerStrengthening,
      upgradeLevel: prev.producerStrengthening.upgradeLevel + 1,
    },
  }));
  if (props.setProgressTimes) {
    props.setProgressTimes.forEach((setProgressTime) => {
      setProgressTime(0);
    });
  }
}

export function executeBulkProducerStrengthening(props: {
  setGame: React.Dispatch<React.SetStateAction<Game>>;
  game: Game;
  setProgressTimes: any[];
  bulk: number;
}) {
  const multiplier = props.game.producerStrengthening.multiplier;
  const particleStartValue = getParticleStartValue(props.game);

  props.setGame((prev: Game) => ({
    ...prev,
    producers: prev.producers.map((producer: Producer, index: number) => {
      const newMultiplier = producer.multiplier.times(
        multiplier.pow(props.bulk)
      );
      return {
        ...producer,
        value: index === 0 ? new Decimal(particleStartValue) : new Decimal(0),
        upgradeLevel: 0,
        produceTimeProgress: 0,
        multiplier: newMultiplier,
        unlocked: true,
      };
    }),
    producerStrengthening: {
      ...prev.producerStrengthening,
      upgradeLevel: prev.producerStrengthening.upgradeLevel + props.bulk,
    },
  }));
  if (props.setProgressTimes) {
    props.setProgressTimes.forEach((setProgressTime) => {
      setProgressTime(0);
    });
  }
}

export function executeStrengtheningBoost(
  game: Game,
  setGame: React.Dispatch<React.SetStateAction<Game>>,
  setProgressTimes: any[]
) {
  const particleStartValue = getParticleStartValue(game);
  setGame((prev: Game) => ({
    ...prev,
    strengtheningBoost: {
      ...prev.strengtheningBoost,
      upgradeLevel: prev.strengtheningBoost.upgradeLevel + 1,
      alreadySeen: true,
    },
    producerStrengthening: {
      ...prev.producerStrengthening,
      upgradeLevel: 0,
      multiplier: calculateNewProducerStrengthiningMultiplier(prev),
    },
    producers: prev.producers.map((producer: Producer, index: number) => {
      const unlocked = producerUnlocked(
        index,
        producer.unlocked,
        isMoleculeUnlocked("Nitrogen", prev)
      );
      return {
        ...producer,
        value: index === 0 ? new Decimal(particleStartValue) : new Decimal(0),
        upgradeLevel: 0,
        produceTimeProgress: 0,
        multiplier: new Decimal(1),
        unlocked,
      };
    }),
  }));
  if (setProgressTimes) {
    setProgressTimes.forEach((setProgressTime) => {
      setProgressTime(0);
    });
  }
}

export function executeMoleculeCreation(
  game: Game,
  setGame: React.Dispatch<React.SetStateAction<Game>>,
  setProgressTimes: any[]
) {
  const startParticleValue = getParticleStartValue(game);
  const keepShopUpgrades = game.unlockedAchievementIds.has(13);
  let addedMolecules = calculateExpectedMoleculeValue(game);
  setGame((prev: Game) => {
    const moleculeCreation = {
      molecules: addedMolecules,
      startedAt: prev.molecule.startedAt,
      endedAt: Date.now(),
    };

    const newLast10MoleculeCreations = [
      moleculeCreation,
      ...prev.last10MoleculeCreations,
    ];
    if (newLast10MoleculeCreations.length > 10) {
      newLast10MoleculeCreations.pop();
    }
    return {
      ...prev,
      producers: prev.producers.map((producer: Producer, index: number) => {
        const unlocked = producerUnlocked(
          index,
          producer.unlocked,
          isMoleculeUnlocked("Nitrogen", prev)
        );
        return {
          ...producer,
          value: index === 0 ? new Decimal(startParticleValue) : new Decimal(0),
          upgradeLevel: 0,
          produceTimeProgress: 0,
          multiplier: new Decimal(1),
          unlocked,
          automateCollect: keepShopUpgrades ? producer.automateCollect : false,
          produceTime: keepShopUpgrades
            ? producer.produceTime
            : newGame.producers[index].produceTime,
          speedUpgradeLevel: keepShopUpgrades ? producer.speedUpgradeLevel : 0,
          automateBuy: keepShopUpgrades ? producer.automateBuy : false,
        };
      }),
      strengtheningBoost: {
        ...prev.strengtheningBoost,
        upgradeLevel: 0,
      },
      producerStrengthening: {
        ...prev.producerStrengthening,
        upgradeLevel: 0,
        multiplier: psStartMultiplier,
      },
      molecule: {
        ...prev.molecule,
        value: prev.molecule.value.plus(addedMolecules),
        unlocked: true,
        creations: addedMolecules.gt(0)
          ? prev.molecule.creations + 1
          : prev.molecule.creations,
        startedAt: Date.now(),
      },
      last10MoleculeCreations: addedMolecules.gt(0)
        ? newLast10MoleculeCreations
        : prev.last10MoleculeCreations,
    };
  });
  setProgressTimes.forEach((setProgressTime) => {
    setProgressTime(0);
  });
}

function producerUnlocked(
  index: number,
  prevValue: boolean,
  nitrogenUnlocked: boolean
): boolean {
  if (nitrogenUnlocked) return true;
  if (index <= 4) return true;
  return false;
}
